import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { Breadcrumbs } from '../../../../components/Breadcrumbs';
import { Checkbox } from '../../../../components/Checkbox';
import { Input } from '../../../../components/Input';
import { colors } from '../../../../constants/Colors';
import { typography } from '../../../../helpers/styles/typography';
import { units } from '../../../../helpers/styles/units';
import { FontWeight } from '../../../../constants/Styles/fontWeight';
import { Button } from '../../../../components/Button';
import { TypesButton } from '../../../../constants/ButtonTypes';
import { SizesButton } from '../../../../constants/SizeButton';
import { routes } from '../../../../constants/RouterPath';
import { PageTitle } from '../../../../components/PageTitle';
import { UploadPhotoWithMiniature } from '../../../../components/UploadPhotoWithMiniature';
import { UploadFile } from '../../../../types/Endpoints/files/FilesContract';
import Switch from '../../../../components/sw';
import { EditCatalogBrandItem } from '../../../../types/Endpoints/brands/BrandsContracts';
import { editBrandBreadcrumbs } from '../../../../constants/BreadCrumbs/editBrandBreadcrumbs';
import { createCatalogBrands } from '../../../../actions/brands';
import { ButtonRoot } from '../../../../components/StyledComponents';
import { isEqualObject } from '../../../../helpers/isEqual';
import { addNotification } from '../../../../actions/notifications';
import { TypesSnackbars } from '../../../../types/TypesSnackbars';
import { TypesIcon } from '../../../../types/TypesIcon';
import { Textarea } from '../../../../components/TextArea';

const StyledRootWrapper = styled.div`
  position: relative;
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;

  padding-left: ${units(12)};
  margin-bottom: ${units(63)};
`;

const StyledRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(40)};
  padding-top: ${units(20)};
`;

const StyledMainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(12)};

  max-width: ${units(322)};
  width: 100%;
`;

const BlockWrapper = styled(StyledMainWrapper)`
  gap: ${units(20)};
`;

const StyledIdInfo = styled.div`
  font-weight: ${FontWeight.MEDIUM};

  color: ${colors.greyDark};
  background-color: ${colors.light};

  ${typography(10)};
`;

const StyledInput = styled(Input)`
  font-weight: ${FontWeight.REGULAR};
  letter-spacing: 0.8px;

  background-color: ${colors.light};

  ${typography(10)};
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const ButtonWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${units(8)};

  padding: ${units(12)};

  max-width: ${units(322)};
`;

const StyledOrderWrapper = styled.div`
  width: ${units(88)};
`;

const StyledSubtitle = styled.span`
  font-weight: ${FontWeight.MEDIUM};

  ${typography(10)};
`;

const SeoFieldsWrapper = styled(StyledMainWrapper)`
    margin-bottom: ${units(10)};
`;

interface CreateBrandModel {
    name: string;
    seoH1: string;
    seoDescription: string;
    seoTitle: string;
    externalId: string;
    urn: string;
    sortNumber: number;
    isShowOnSite: boolean;
    isShowOnMobile: boolean;
    isShowInCollaboration: boolean;
    brandLogoFileId: UploadFile;
    brandSiteFileId: UploadFile;
    brandMobileFileId: UploadFile;
}

export const CreateBrand = () => {
    const history = useHistory();
    const dispatch = useDispatch();

    const handleSubmit = (values: CreateBrandModel) => {
        const requestData: EditCatalogBrandItem = {
            name: values.name,
            seoH1: values.seoH1,
            seoDescription: values.seoDescription,
            seoTitle: values.seoTitle,
            urn: values.urn,
            sortNumber: values.sortNumber,
            externalId: values.externalId,
            isShowOnSite: values.isShowOnSite,
            isShowOnMobile: values.isShowOnMobile,
            isShowInCollaboration: values.isShowInCollaboration,
            brandLogoFileId: values.brandLogoFileId?.id || null,
            brandSiteFileId: values.brandSiteFileId?.id || null,
            brandMobileFileId: values.brandMobileFileId?.id || null,
        };

        dispatch(createCatalogBrands(requestData));
    };

    const form = useFormik<CreateBrandModel>({
        onSubmit: handleSubmit,
        initialValues: {
            isShowOnSite: true,
            isShowOnMobile: true,
            isShowInCollaboration: false,
        } as CreateBrandModel,
    });

    const handleToggle = (value: boolean) => {
        form.values.isShowInCollaboration = value;
    };

    const handleResetForm = (values: CreateBrandModel) => {
        history.push(routes.brands);
        const data = {
            isShowOnSite: true,
            isShowOnMobile: true,
        } as CreateBrandModel;
        const equal = isEqualObject(data, values);

        if (!equal) {
            dispatch(
                addNotification({
                    type: TypesSnackbars.ERROR,
                    message: {
                        title: 'Изменения отменены',
                        description: '',
                    },
                    icon: TypesIcon.SNACK_ERROR,
                }),
            );
        }
    };

    return (
        <StyledRootWrapper>
            <StyledWrapper>
                <PageTitle>Бренды</PageTitle>
                <Breadcrumbs breadcrumbs={editBrandBreadcrumbs('Добавить')} />
                <StyledRoot>
                    <BlockWrapper>
                        <SeoFieldsWrapper>
                            <StyledIdInfo>SEO</StyledIdInfo>
                            <Textarea
                                name="seoH1"
                                onChange={form.handleChange}
                                value={form.values.seoH1}
                                placeholder="H1"
                                maxLength={255}
                                title="H1"
                                isTouched={form.touched.seoH1}
                                error={form.errors.seoH1}
                            />
                            <Textarea
                                name="seoTitle"
                                onChange={form.handleChange}
                                value={form.values.seoTitle}
                                placeholder="Title"
                                maxLength={255}
                                title="Title"
                                isTouched={form.touched.seoTitle}
                                error={form.errors.seoTitle}
                            />
                            <Textarea
                                name="seoDescription"
                                onChange={form.handleChange}
                                value={form.values.seoDescription}
                                placeholder="Description"
                                maxLength={255}
                                title="Description"
                                isTouched={form.touched.seoDescription}
                                error={form.errors.seoDescription}
                            />
                        </SeoFieldsWrapper>
                        <StyledMainWrapper>
                            <StyledIdInfo>Основное</StyledIdInfo>
                            <StyledInput
                                name="name"
                                onChange={form.handleChange}
                                value={form.values.name}
                                placeholder="Название"
                                typeInput="text"
                                maxLength={255}
                                title="Название"
                                isTouched={form.touched.name}
                                error={form.errors.name}
                            />

                            <StyledInput
                                name="urn"
                                onChange={form.handleChange}
                                value={form.values.urn}
                                placeholder="URL"
                                typeInput="text"
                                title="URL"
                                isTouched={form.touched.urn}
                                error={form.errors.urn}
                            />

                            <StyledInput
                                name="externalId"
                                onChange={form.handleChange}
                                value={form.values.externalId}
                                placeholder="Элемент выгрузки"
                                typeInput="text"
                                maxLength={255}
                                title="Элемент выгрузки"
                                isTouched={form.touched.externalId}
                                error={form.errors.externalId}
                            />

                            <StyledOrderWrapper>
                                <StyledInput
                                    name="sortNumber"
                                    onChange={form.handleChange}
                                    value={form.values.sortNumber}
                                    placeholder="Номер при показе"
                                    typeInput="number"
                                    maxLength={255}
                                    title="Номер при показе"
                                    isTouched={form.touched.sortNumber}
                                    error={form.errors.sortNumber}
                                />
                            </StyledOrderWrapper>
                        </StyledMainWrapper>
                        <StyledMainWrapper>
                            <StyledIdInfo>Отображение</StyledIdInfo>
                            <Checkbox
                                name="isShowOnSite"
                                title="Отображение на сайте"
                                checked={form.values.isShowOnSite}
                                onChange={form.handleChange}
                            />

                            <Checkbox
                                name="isShowOnMobile"
                                title="Отображение в мобильном приложении"
                                checked={form.values.isShowOnMobile}
                                onChange={form.handleChange}
                            />
                        </StyledMainWrapper>
                        <StyledMainWrapper>
                            <StyledSubtitle>Сотрудничество</StyledSubtitle>
                            <Switch
                                name="isShowCollectionBanner"
                                value={form.values.isShowInCollaboration}
                                onChange={handleToggle}
                                type="checkbox"
                                title
                                inactiveTitle="Выключено"
                                activeTitle="Включено"
                            />
                        </StyledMainWrapper>
                    </BlockWrapper>
                    <StyledMainWrapper>
                        <UploadPhotoWithMiniature
                            name="brandLogoFileId"
                            title="Логотип бренда"
                            onChange={form.setFieldValue}
                            value={form.values.brandLogoFileId}
                            format="1:1"
                            maxSize={0.1}
                        />
                        <UploadPhotoWithMiniature
                            name="brandSiteFileId"
                            title="Баннер в каталог для сайта"
                            onChange={form.setFieldValue}
                            value={form.values.brandSiteFileId}
                            format="7:1"
                            maxSize={3}
                        />
                    </StyledMainWrapper>
                </StyledRoot>
            </StyledWrapper>
            <ButtonRoot>
                <ButtonWrapper>
                    <StyledButton
                        typeButton={TypesButton.SECONDARY}
                        size={SizesButton.M}
                        onClick={() => handleResetForm(form.values)}
                    >
                        Отменить
                    </StyledButton>
                    <StyledButton
                        typeButton={TypesButton.PRIMARY}
                        size={SizesButton.M}
                        onClick={form.submitForm}
                    >
                        Сохранить изменения
                    </StyledButton>
                </ButtonWrapper>
            </ButtonRoot>
        </StyledRootWrapper>
    );
};
